<template>
  <SoDialog
    v-model="value"
    :max-width="600"
    :loading="current.getMeterpointCancellationReason.isFetching"
    :card-title="
      existingCancellationReason
        ? 'Update cancellation reason'
        : 'Cancel Switch'
    "
  >
    <SoForm
      v-if="!current.getMeterpointCancellationReason.isFetching"
      v-model="isValid"
      @submit="submitCancellation"
    >
      <SoAlert v-if="existingCancellationReason" type="info" class="mb-4">
        <p>Current cancellation reason:</p>
        <p>{{ existingCancellationReason }}</p>
      </SoAlert>
      <div class="mb-4">
        <SoSelect
          v-model="selectedCancellationCategory"
          :items="formatDropdownItems(cancellationReasons)"
          :disabled="isDisabled"
          :rules="[rules.required]"
          :label="
            existingCancellationReason
              ? 'Update reason for cancellation'
              : 'Reason for cancellation'
          "
          @update:model-value="
            ;((selectedcancellationReason1 = ''),
              (selectedcancellationReason2 = ''),
              (cancellationReasonFreeText = ''))
          "
        />
        <SoSelect
          v-if="selectedCancellationCategory && cancellationReasons1?.length"
          v-model="selectedcancellationReason1"
          :items="formatDropdownItems(cancellationReasons1)"
          :disabled="isDisabled"
          :rules="[rules.required]"
          class="mt-4"
          @change="
            ;((selectedcancellationReason2 = ''),
              (cancellationReasonFreeText = ''))
          "
        />
        <SoSelect
          v-if="selectedcancellationReason1 && cancellationReasons2?.length"
          v-model="selectedcancellationReason2"
          :items="formatDropdownItems(cancellationReasons2)"
          :disabled="isDisabled"
          :rules="[rules.required]"
          class="mt-4"
          @change="cancellationReasonFreeText = ''"
        />
        <SoTextarea
          v-if="showTextArea"
          v-model="cancellationReasonFreeText"
          :disabled="isDisabled"
          :rules="[rules.required]"
          label="Please specify"
          class="mt-2"
        />
      </div>
      <p v-if="!existingCancellationReason">
        You’re about to cancel the customer’s switch to So Energy for all fuels.
        Are you sure?
      </p>
      <SoAlert
        v-if="
          cancelAccountMutator.isSuccess &&
          createCancellationReasonMutator.isSuccess
        "
        title="Cancellation successfully initiated"
        class="mt-5"
        type="success"
        data-testid="account-cancellation-success-alert"
      />
      <SoAlert
        v-if="updateCancellationReasonMutator.isSuccess"
        title="Cancellation reason updated"
        type="success"
        class="mt-5"
        data-testid="update-cancellation-reason-success-alert"
      />
      <SoAlert
        v-if="cancelAccountMutator.isError"
        title="Cancellation unsuccessful"
        type="error"
        class="mt-5"
        data-testid="account-cancellation-error-alert"
      />
      <SoAlert
        v-if="updateCancellationReasonMutator.isError"
        title="Cancellation reason update unsuccessful"
        type="error"
        class="mt-5"
        data-testid="update-cancellation-reason-error-alert"
      />
      <div class="d-flex mt-8">
        <v-spacer />
        <SoButton
          :loading="isLoading"
          :disabled="isDisabled"
          class="mr-2"
          :title="existingCancellationReason ? 'Update' : 'Yes, cancel switch'"
          type="primary"
          large
          submit
        />
        <SoButton text="Close" title="Close" large @click="value = false" />
      </div>
    </SoForm>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { errorReporter } from "@/services/ErrorService"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import rules from "@/validators/rules"
  import { MeterpointCancellationReason } from "@/types/MeterpointCancellationReason"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoTextarea from "@/components/formControls/SoTextarea.vue"
  import {
    ICancellationReason,
    cancellationReasons,
  } from "./cancellationReasons"
  import { CancellationReasonMutator } from "@/mutators/cancelAccount/CancellationReasonMutator"
  import { CancelAccountMutator } from "@/mutators/cancelAccount/CancelAccountMutator"

  export default defineComponent({
    components: {
      SoDialog,
      SoSelect,
      SoButton,
      SoAlert,
      SoForm,
      SoTextarea,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        isValid: false,
        current: useCurrentAccountStore(),
        cancelAccountMutator: CancelAccountMutator.cancelAccount(),
        createCancellationReasonMutator: CancellationReasonMutator.create(),
        updateCancellationReasonMutator: CancellationReasonMutator.update(),
        selectedCancellationCategory: "",
        selectedcancellationReason1: "",
        selectedcancellationReason2: "",
        cancellationReasonFreeText: "",
        cancellationReasons,
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      rules() {
        return rules
      },
      cancellationReasons1() {
        if (!this.selectedCancellationCategory) {
          return []
        }
        const cancellationReasons = (
          this.cancellationReasons as ICancellationReason[]
        ).find((reason) => reason.id === this.selectedCancellationCategory)

        return cancellationReasons?.children || []
      },
      cancellationReasons2() {
        if (!this.selectedcancellationReason1) {
          return []
        }
        const cancellationReasons = (
          this.cancellationReasons1 as ICancellationReason[]
        ).find((reason) => reason.id === this.selectedcancellationReason1)

        return cancellationReasons?.children || []
      },
      isLoading(): boolean {
        return (
          this.cancelAccountMutator.isPending ||
          this.createCancellationReasonMutator.isPending ||
          this.updateCancellationReasonMutator.isPending
        )
      },
      isDisabled(): boolean {
        return (
          this.isLoading ||
          this.cancelAccountMutator.isSuccess ||
          this.updateCancellationReasonMutator.isSuccess
        )
      },
      formattedCancellationReason() {
        if (!this.selectedCancellationCategory) return ""
        let cancellationReasons =
          this.selectedcancellationReason1 || this.selectedCancellationCategory
        if (this.selectedcancellationReason2) {
          cancellationReasons += ` - ${this.selectedcancellationReason2}`
        }
        if (this.cancellationReasonFreeText) {
          cancellationReasons += ` - ${this.cancellationReasonFreeText}`
        }
        return cancellationReasons
      },
      existingCancellationReason() {
        const cancellationReason =
          this.current.getMeterpointCancellationReason.data
        if (cancellationReason && Object.keys(cancellationReason).length > 0) {
          for (const obj of Object.values(cancellationReason)) {
            if (obj?.reasonDetail) {
              return obj.reasonDetail
            }
          }
        }
        return null
      },
      showTextArea() {
        const cancellationReasonFreeText = [
          "Other",
          "Additional needs",
          "Technical issues",
        ]
        const selectedReasons = [
          this.selectedCancellationCategory,
          this.selectedcancellationReason1,
          this.selectedcancellationReason2,
        ]
        return selectedReasons.some((reason) =>
          cancellationReasonFreeText.includes(reason),
        )
      },
    },
    methods: {
      formatDropdownItems(items) {
        return items.map(({ id, text }) => ({
          value: id,
          title: text,
        }))
      },
      async submitCancellation() {
        if (!this.isValid) return

        const meterpointIds = this.current.meterpoints.data.map(
          (meterpoint) => meterpoint.id,
        )

        const cancellationReasons =
          this.current.getMeterpointCancellationReason.data

        if (this.existingCancellationReason) {
          await this.updateCancellationReason(cancellationReasons)
        } else {
          await this.cancelAccount(meterpointIds)
        }
      },
      async updateCancellationReason(
        cancellationReasons: MeterpointCancellationReason,
      ) {
        for (const cancellationReason of Object.values(cancellationReasons)) {
          if (!cancellationReason) continue
          try {
            await this.updateCancellationReasonMutator.mutateAsync({
              id: cancellationReason.id,
              reasonDetail: this.formattedCancellationReason,
            })
          } catch (error) {
            errorReporter.report(error)
            console.error(error)
          }
        }
      },
      async cancelAccount(meterpointIds: number[]) {
        const billingAccountId = this.current.coreId.data
        if (!billingAccountId) throw new Error("Billing account ID is required")

        try {
          await this.cancelAccountMutator.mutateAsync({
            reason: this.formattedCancellationReason,
          })
          await Promise.all(
            meterpointIds.map(
              async (meterpointId) =>
                await this.createCancellationReasonMutator.mutate({
                  billingAccountId,
                  meterpointId,
                  details: this.formattedCancellationReason,
                  channel: "Nova",
                }),
            ),
          )
        } catch (error) {
          errorReporter.report(error)
          console.error(error)
        }
      },
    },
  })
</script>
